import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { Button, Spacer } from '../../components/atoms';
import { LOGIN } from '../../navigation/routes';
import { resendSignupOtp, verifyOtp } from '../../network/services/auth';
import { useMutation } from '../../network/mutations/useMutationHook';
import { userIsLoggedIn, userRoleAtom } from '../../store/atoms/userAtom';
import type { CustomerResendOtpResponse, CustomerVerifyOtpResponse } from '../../TYPES/api.body';
import { COMMONTYPES } from '../../TYPES/event.types';
import { isOtpRequired, persistCustomerAuth, resolvePostAuthPath } from '../../utils/customerAuth';
import { useCounter } from '../../utils/counter';
import { enqueueSnackbar } from 'notistack';
import { AuthContainer, Line, OtpContainer } from './styles';

const Otp = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  const handleChange = (index: number, e: COMMONTYPES['inputChange']) => {
    const newOtp = [...otp];
    newOtp[index] = e.target.value;

    setOtp(newOtp);

    if (e.target.value && index < 5) {
      refs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (!e.currentTarget.value && index > 0) {
        refs[index - 1].current?.focus();
      }
    }
  };

  const { email: emailParam } = useParams();
  const email = emailParam ? decodeURIComponent(emailParam) : undefined;

  const [countMultipler, setCountMultipler] = useState(120);

  const location = useLocation();
  const forgotPassword = location.pathname.includes('forgot-password');

  const navigate = useNavigate();
  const setUserRole = useSetRecoilState(userRoleAtom);
  const setUserStatus = useSetRecoilState(userIsLoggedIn);

  const { counter, setCounter } = useCounter();

  const stringOtp = otp.join('');

  const { mutateAsync, isLoading } = useMutation(verifyOtp, 'OTP');

  const completeSignupSession = (data: CustomerVerifyOtpResponse) => {
    if (!data.access_token) {
      enqueueSnackbar('Verification succeeded but no session token was returned. Please log in.', {
        variant: 'warning'
      });
      navigate(LOGIN);
      return;
    }
    persistCustomerAuth({ access_token: data.access_token, user: data.user });
    const role = (data.user as { role?: string } | undefined)?.role;
    if (role) {
      setUserRole(role);
    }
    setUserStatus(true);
    enqueueSnackbar(data.description ?? 'Email verified successfully', { variant: 'success' });
    navigate(resolvePostAuthPath());
  };

  const _verifyOtp = async (e: COMMONTYPES['buttonClick']) => {
    e.preventDefault();
    if (!email) return;

    try {
      const { data } = await mutateAsync({ otp: stringOtp, email });
      const verifyResponse = data as CustomerVerifyOtpResponse;

      if (verifyResponse.status !== 'success') {
        enqueueSnackbar(verifyResponse.message ?? 'Verification failed', { variant: 'error' });
        return;
      }

      if (forgotPassword) {
        navigate(`/new-password?otp=${stringOtp}&email=${encodeURIComponent(email)}`);
        return;
      }

      completeSignupSession(verifyResponse);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message ?? 'OTP not found or expired. Request a new code.',
        { variant: 'error' }
      );
    }
  };

  const reSendOtp = async (e: COMMONTYPES['spanClick']) => {
    e.preventDefault();
    if (!email || forgotPassword) return;

    try {
      const { data } = await resendSignupOtp({ email });
      const resendResponse = data as CustomerResendOtpResponse;

      if (!isOtpRequired(resendResponse)) {
        enqueueSnackbar(resendResponse.message ?? 'OTP is not required. Please log in.', { variant: 'info' });
        navigate(LOGIN);
        return;
      }

      enqueueSnackbar(resendResponse.message ?? 'A new OTP has been sent to your email.', { variant: 'success' });
      setCounter(countMultipler);
      setCountMultipler(prev => prev + 60);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message ?? 'Could not resend OTP', { variant: 'error' });
    }
  };

  useEffect(() => {
    setCounter(60);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabled = otp.some(digit => digit === '');

  return (
    <AuthContainer>
      <h1>Enter OTP</h1>
      <p className="option">Please Enter the OTP sent to {email?.toLowerCase()} </p>
      <Spacer height="2rem" />
      <form className="otp-form">
        <OtpContainer>
          {otp.map((digit, index) => (
            <React.Fragment key={index}>
              <input
                name="otp"
                type="text"
                value={digit}
                maxLength={1}
                onChange={e => handleChange(index, e)}
                onKeyDown={e => handleKeyDown(index, e)}
                ref={refs[index]}
              />
              {index === 2 && <Line style={{ width: '4rem' }} />}
            </React.Fragment>
          ))}
        </OtpContainer>
        {!forgotPassword ? (
          <div className="option">
            {counter > 0 ? (
              <div className="resend">Resend in {counter}s </div>
            ) : (
              <span onClick={reSendOtp}>Resend OTP</span>
            )}
          </div>
        ) : null}
        <Spacer height="2rem" />
        <div className="buttonContainer">
          <Button isLoading={isLoading} type="submit" onClick={_verifyOtp} disabled={disabled}>
            Verify OTP
          </Button>
        </div>
      </form>
    </AuthContainer>
  );
};

export default Otp;
